import { useEffect, useMemo, useState } from "react";
import { Alert, Badge, Button, Col, Form, Row, Spinner } from "react-bootstrap";
import { Check, Search } from "lucide-react";
import { CallAPIGetPromise } from "Common/commaon";

type SicCodeResult = {
  sicCode: string;
  description: string;
};

type Props = {
  value: string;
  onChange: (sicCode: string) => void;
  isInvalid?: boolean;
  productHint?: string;
};

function getSearchErrorMessage(err: unknown) {
  const error = err as { response?: { data?: { message?: string } }; message?: string };
  return error?.response?.data?.message || error?.message || "Unable to search SIC codes";
}

export default function SicCodeSearchField({ value, onChange, isInvalid, productHint }: Props) {
  const [searchPhrase, setSearchPhrase] = useState("");
  const [results, setResults] = useState<SicCodeResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchError, setSearchError] = useState("");
  const [selectedDescription, setSelectedDescription] = useState("");

  const selectedFromResults = useMemo(
    () => results.find((item) => item.sicCode === value),
    [results, value]
  );

  const displayDescription = selectedDescription || selectedFromResults?.description || "";

  useEffect(() => {
    if (selectedFromResults?.description) {
      setSelectedDescription(selectedFromResults.description);
    }
  }, [selectedFromResults]);

  const runSearch = async (phrase: string) => {
    const query = phrase.trim();
    if (!query) {
      setSearchError("Enter a business type keyword to search.");
      setResults([]);
      return;
    }

    setLoading(true);
    setSearchError("");
    try {
      const response = (await CallAPIGetPromise(
        `/v2/api/deluxe/merchant/sic-codes?phrase=${encodeURIComponent(query)}`
      )) as { data?: SicCodeResult[] };
      const list = Array.isArray(response?.data) ? response.data : [];
      setResults(list);
      if (!list.length) {
        setSearchError("No SIC codes matched that keyword. Try a broader term.");
      }
    } catch (err) {
      setResults([]);
      setSearchError(getSearchErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handleSelect = (item: SicCodeResult) => {
    onChange(String(item.sicCode || "").replace(/\D/g, "").slice(0, 4));
    setSelectedDescription(item.description || "");
    setSearchError("");
  };

  return (
    <div
      className="rounded-3 border p-3"
      style={{ background: "#f8fafc", borderColor: isInvalid ? "#dc3545" : "#e2e8f0" }}
    >
      <div className="d-flex flex-wrap align-items-start justify-content-between gap-2 mb-3">
        <div>
          <Form.Label className="mb-1 fw-semibold">SIC Code</Form.Label>
          <div className="text-muted small">4-digit industry classification required by Deluxe</div>
        </div>
        {value ? (
          <Badge bg="success" className="d-inline-flex align-items-center gap-1">
            <Check size={14} /> Code {value}
          </Badge>
        ) : null}
      </div>

      <Row className="g-2 align-items-start mb-3">
        <Col xs={12} sm={4} md={3}>
          <Form.Label className="small text-muted mb-1">Selected code</Form.Label>
          <Form.Control
            placeholder="e.g. 5812"
            value={value}
            maxLength={4}
            inputMode="numeric"
            onChange={(e) => {
              onChange(e.target.value.replace(/\D/g, "").slice(0, 4));
              setSelectedDescription("");
            }}
            isInvalid={isInvalid}
            className={isInvalid ? "is-invalid" : ""}
            style={{ fontWeight: 600, letterSpacing: "0.05em" }}
          />
        </Col>
        <Col xs={12} sm={8} md={9}>
          <Form.Label className="small text-muted mb-1">Industry description</Form.Label>
          <div
            className="rounded-2 border bg-white px-3 py-2 small"
            style={{ minHeight: 38, color: displayDescription ? "#0f172a" : "#94a3b8" }}
          >
            {displayDescription || "Search below and click a result to fill this in."}
          </div>
        </Col>
      </Row>

      <div className="border-top pt-3">
        <Form.Label className="small fw-semibold mb-2">Search SIC codes by business type</Form.Label>
        <Row className="g-2 align-items-stretch">
          <Col xs={12} md={9}>
            <Form.Control
              placeholder="e.g. restaurant, home renovation, travel agency"
              value={searchPhrase}
              maxLength={200}
              onChange={(e) => setSearchPhrase(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  runSearch(searchPhrase);
                }
              }}
            />
          </Col>
          <Col xs={12} md={3} className="d-grid">
            <Button variant="primary" onClick={() => runSearch(searchPhrase)} disabled={loading}>
              {loading ? (
                <Spinner animation="border" size="sm" />
              ) : (
                <>
                  <Search size={16} className="me-1" /> Search
                </>
              )}
            </Button>
          </Col>
        </Row>

        {productHint ? (
          <Button
            type="button"
            variant="link"
            className="px-0 mt-1 small text-decoration-none"
            onClick={() => {
              setSearchPhrase(productHint);
              runSearch(productHint);
            }}
          >
            Use products/services description as search keyword
          </Button>
        ) : null}
      </div>

      {searchError ? (
        <Alert variant="warning" className="mt-3 mb-0 py-2 small">
          {searchError}
        </Alert>
      ) : null}

      {results.length > 0 && (
        <div className="mt-3">
          <div className="small text-muted mb-2">
            {results.length} result{results.length === 1 ? "" : "s"} — click one to select
          </div>
          <div
            className="rounded-3 border bg-white overflow-hidden shadow-sm"
            style={{ maxHeight: 240, overflowY: "auto" }}
          >
            {results.map((item, index) => {
              const isSelected = value === item.sicCode;
              return (
                <button
                  key={`${item.sicCode}-${item.description}`}
                  type="button"
                  className={`w-100 text-start border-0 px-3 py-2 d-flex align-items-start gap-3 ${
                    index < results.length - 1 ? "border-bottom" : ""
                  }`}
                  style={{
                    background: isSelected ? "#eff6ff" : "#fff",
                    borderLeft: isSelected ? "3px solid #0d6efd" : "3px solid transparent",
                  }}
                  onClick={() => handleSelect(item)}
                >
                  <span
                    className="fw-bold flex-shrink-0"
                    style={{ width: 48, color: isSelected ? "#0d6efd" : "#334155" }}
                  >
                    {item.sicCode}
                  </span>
                  <span className="flex-grow-1 small" style={{ color: "#475569", lineHeight: 1.45 }}>
                    {item.description}
                  </span>
                  {isSelected ? (
                    <Badge bg="primary" className="flex-shrink-0">
                      Selected
                    </Badge>
                  ) : null}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {isInvalid ? (
        <div className="invalid-feedback d-block mt-2">SIC code is required.</div>
      ) : null}
    </div>
  );
}
